import { chain, path, prop, values } from 'ramda';
import { MediaType } from '../../constants/MediaType';
import { SourceName } from '../../constants/SourceName';
import { NextCallData } from '../../utils/NextCallData';
import { toError } from './parsers/error';
import { parseItems } from './parsers/items';
import { parseMultiSearchItems } from './parsers/multiSearchItems';
import { parseFilters } from './utils';
import { toItem } from './mappers/item';
const SEARCH_LIMITS = {
  MAIN: 50,
  OTHER: 49
};
export function initItems(settings, http) {
  const {
    isPublicMediaScoringProfileEnabled = false,
    isUseSmallerLimitForPublicSearchEnabled = false
  } = settings;
  const nextCallData = new NextCallData();
  function requestItems(action, requestOptions, thumbSizeData, headers, query, isMultiSearch, folderId) {
    if (query === void 0) {
      query = null;
    }
    if (isMultiSearch === void 0) {
      isMultiSearch = false;
    }
    if (nextCallData.isNextCallDataExists({
      cursor: action
    }, query)) {
      return nextCallData.getPromiseAndReset();
    }
    const requestPromise = http.get(settings.apiUrl + action, requestOptions, {
      withCredentials: true,
      headers: headers || {}
    }).then(response => isMultiSearch ? parseMultiSearchItems(response, settings, thumbSizeData, folderId) : parseItems(response, settings, thumbSizeData)).catch(toError);
    return requestPromise;
  }

  /**
   * Gets list of items
   * @param folderId
   * @param options
   * @returns {Promise}
   * @fulfill {Items}
   * @reject {Error}
   */
  function list(folderId, options) {
    options = options || {};
    options.paging = options.paging || {};
    const {
      cursor,
      size
    } = options.paging;
    if (cursor) {
      const itemsPromise = requestItems(cursor, {}, options.thumbnails, options.headers);
      if (settings.prefetchNextPage) {
        itemsPromise.then(response => {
          const {
            cursor
          } = response.paging;
          if (!cursor) {
            return;
          }
          nextCallData.set({
            cursor
          }, requestItems(cursor, {}, options.thumbnails, options.headers), null, folderId);
        });
      }
      return itemsPromise;
    }
    const {
      filters = {}
    } = options;
    const tags = parseFilters(filters);
    if (settings.wixUser) {
      folderId = `${folderId}:wix`;
    }
    const requestOptions = {
      guid: folderId || null,
      mediaType: options.mediaType || MediaType.Picture,
      language: options.language,
      pageSize: size || settings.itemsLimit,
      ...(options.cacheKiller ? {
        _ck: new Date().getTime()
      } : {}),
      ...(isPublicMediaScoringProfileEnabled ? {
        new_scoring_profile: true
      } : {})
    };
    if (options.country) {
      requestOptions.country = options.country;
    }
    if (tags.length) {
      requestOptions.tags = tags;
    }
    const itemsPromise = requestItems('/light_items', requestOptions, options.thumbnails, options.headers);
    if (settings.prefetchNextPage) {
      itemsPromise.then(response => {
        const {
          cursor
        } = response.paging;
        if (!cursor) {
          return;
        }
        nextCallData.set({
          cursor
        }, requestItems(cursor, {}, options.thumbnails, options.headers), null, folderId);
      });
    }
    return itemsPromise;
  }
  function createSearchMethod(pathname, isMultiSearch) {
    if (isMultiSearch === void 0) {
      isMultiSearch = false;
    }
    return function (folderId, query, options) {
      options = options || {};
      options.paging = options.paging || {};
      const {
        cursor,
        size
      } = options.paging;
      let folderIdSuffix = '';
      if (settings.wixUser) {
        folderIdSuffix = ':wix';
      }
      if (cursor) {
        const itemsPromise = requestItems(cursor, {}, options.thumbnails, options.headers, null, isMultiSearch, folderId);
        if (settings.prefetchNextPage) {
          itemsPromise.then(response => {
            const {
              cursor
            } = response.paging;
            if (!cursor) {
              return;
            }
            nextCallData.set({
              cursor
            }, requestItems(cursor, {}, options.thumbnails, options.headers, null, isMultiSearch, folderId), query, `${folderId}${folderIdSuffix}`);
          });
        }
        return itemsPromise;
      }
      const {
        filters = {}
      } = options;
      const tags = parseFilters(filters);
      const guid = isMultiSearch ? options.categories : folderId;
      const requestOptions = {
        guid: guid ? `${guid}${folderIdSuffix}` : null,
        mediaType: options.mediaType || MediaType.Picture,
        language: options.language,
        searchString: query || '',
        pageSize: size || settings.itemsLimit,
        semantic: options.semantic,
        newScoringProfile: isPublicMediaScoringProfileEnabled
      };
      if (tags.length) {
        requestOptions.tags = tags;
      }
      if (options.relatedSource) {
        requestOptions.relatedSource = options.relatedSource;
        requestOptions.searchString = '';
      }
      if (options.country) {
        requestOptions.country = options.country;
      }
      const itemsPromise = requestItems(pathname, requestOptions, options.thumbnails, options.headers, null, isMultiSearch, folderId);
      if (settings.prefetchNextPage) {
        itemsPromise.then(response => {
          const {
            cursor
          } = response.paging;
          if (!cursor) {
            return;
          }
          nextCallData.set({
            cursor
          }, requestItems(cursor, requestOptions, options.thumbnails, options.headers, null, isMultiSearch, folderId), query, `${folderId}${folderIdSuffix}`);
        });
      }
      return itemsPromise;
    };
  }
  const search = createSearchMethod('/wix_search');
  const addCategoryIdToItems = (_ref, categoryId) => {
    let {
      data,
      paging
    } = _ref;
    return {
      data: data.map(item => ({
        ...item,
        categoryId,
        hasMoreItemsInThisCategory: paging.cursor !== null
      })),
      paging
    };
  };
  const multiSearch = (folderId, query, options) => Promise.all(options.categories.map(_ref2 => {
    let {
      id,
      mediaType
    } = _ref2;
    const newOptions = {
      ...options,
      mediaType
    };
    return search(id, query, newOptions).then(response => ({
      ...addCategoryIdToItems(response, id),
      categoryId: id
    }));
  })).then(result => ({
    data: result.reduce((accumulator, currentValue) => accumulator.concat(currentValue.data), []),
    paging: result.find(_ref3 => {
      let {
        categoryId
      } = _ref3;
      return categoryId === folderId;
    }).paging
  }));
  let recentSearches;
  const multiSearchV3 = async (folderId, query, options) => {
    const {
      data: {
        public: results,
        searches
      }
    } = await http.post(`${settings.multiSearchApiUrl}/v3/search/multi`, {
      query,
      site_token: settings.siteMediaToken,
      sources: (options.categories || []).map(_ref4 => {
        let {
          id: guid,
          mediaType: media_type
        } = _ref4;
        return {
          guid,
          source: SourceName.Public,
          media_type,
          language: options.language,
          country: options.country,
          page_size: isUseSmallerLimitForPublicSearchEnabled ? guid === folderId ? SEARCH_LIMITS.MAIN : SEARCH_LIMITS.OTHER : SEARCH_LIMITS.MAIN,
          ...(isPublicMediaScoringProfileEnabled ? {
            new_scoring_profile: true
          } : {}),
          ...(options.callSource === SourceName.Public ? {
            call_source: 'mm'
          } : {})
        };
      })
    }, null, {
      withCredentials: true
    });
    recentSearches = searches;
    const data = Object.fromEntries(Object.keys(results).map(categoryId => [categoryId, {
      ...results[categoryId],
      items: (results[categoryId].items || []).map(item => toItem({
        ...item,
        categoryId,
        hasMoreItemsInThisCategory: path(['nextPage', 'cursor'], results[categoryId]) !== null
      }, settings))
    }]));
    const cursor = results[folderId].nextPage && results[folderId].nextPage.replace('/public', '');
    return {
      data: chain(prop('items'), values(data)),
      paging: {
        cursor
      }
    };
  };
  return {
    list,
    search: createSearchMethod('/light_search'),
    searchV2: createSearchMethod('/wix_search'),
    // used in withRelatedItems
    multiSearch: (folderId, query, options) => {
      const isFirstPageRequest = options.paging.cursor === null;
      const firstRequestFn = query ? multiSearchV3 : multiSearch;
      return isFirstPageRequest ? firstRequestFn(folderId, query, options) : search(folderId, query, options).then(response => addCategoryIdToItems(response, folderId));
    },
    getRecentSearches: () => recentSearches
  };
}